import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const WHAT_YOU_GET = [
  "Professional guidance",
  "Unparalleled DS-160 visa form-filling experience",
  "A dedicated support agent",
  "Assistance with hotel booking, itinerary & travel insurance",
];

const US_DOCS = [
  { item: "Visa application form", note: "Fully completed with correct information, printed and signed at the end." },
  { item: "Two recent photos", note: "Taken within the last three months, in compliance with the US visa photo criteria." },
  { item: "Valid passport", note: "No older than ten years, with a minimum validity of three months beyond your planned stay in the United States and at least two blank pages for the visa sticker." },
  { item: "Roundtrip reservation or itinerary", note: "A document that includes dates and flight numbers specifying entry and exit from the US." },
  { item: "Travel health insurance", note: "Evidence that you have purchased health insurance covering medical emergencies for your whole period of stay." },
  { item: "Proof of accommodation", note: "Evidence that shows where you will be staying throughout your time in the US." },
  { item: "Letter of tour organizer", note: "If you will be travelling with a tour agency." },
  {
    item: "Proof of financial means",
    note: "Evidence that you have enough money to support yourself throughout your stay in the US:",
    children: ["Bank account statement", "Sponsorship letter", "A combination of both"],
  },
  {
    item: "Evidence of employment status",
    children: [
      "Employed: employment contract, leave permission from the employer, Income Tax Return",
      "Self-employed: business license, company bank statement for the latest 6 months, Income Tax Return (ITR)",
      "Student: proof of enrollment & No Objection Letter from university",
    ],
  },
  { item: "Travel itinerary", note: "A description of your trip to the US — your purpose of travelling, which places you are going to visit, the time frame and all your personal data." },
  {
    item: "For minors",
    children: [
      "Birth certificate / proof of adoption / custody decree if parents are divorced / death certificate of a parent",
      "Letter of consent from parents, including passport copies of both parents or the legal guardian",
    ],
  },
];

export const UsVisa = () => (
  <div className="bg-[#FAFAFA] text-slate-900 antialiased min-h-screen" data-testid="us-visa-page">
    <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a
          href="/"
          data-testid="us-visa-logo"
          className="font-display text-2xl font-semibold tracking-tight text-slate-900"
        >
          TrioDocz<span className="text-slate-400">.</span>
        </a>
        <a
          href="/#products"
          data-testid="us-visa-back-link"
          className="group flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-slate-600 hover:text-slate-900 transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
          Back to products
        </a>
      </div>
    </header>

    <main className="px-6 md:px-12 pt-40 pb-28">
      <div className="max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
        >
          Visa guide · United States
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl"
        >
          US Tourist Visa{" "}
          <em className="italic text-slate-500">(B1/B2)</em>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="mt-14 grid lg:grid-cols-2 gap-10 pb-14 border-b border-slate-200"
        >
          <div>
            <h2 className="font-display text-xl sm:text-2xl text-slate-900">
              Traveling to America for tourism
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
              A foreign national traveling to the United States for tourism
              needs a visitor visa (B-2) unless qualifying for entry under the
              Visa Waiver Program. Tourism is a short visit for vacation, for
              visiting family and friends, or for medical treatment.
            </p>
            <h2 className="font-display text-xl sm:text-2xl text-slate-900 mt-10">
              Who needs a tourist US visa
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
              Generally, a citizen of a foreign country who wishes to enter the
              United States must first obtain a visa — either a nonimmigrant
              visa for a temporary stay, or an immigrant visa for permanent
              residence. Visitor visas are nonimmigrant visas for persons who
              want to enter the United States temporarily for business (visa
              category B-1), for tourism (visa category B-2), or for a
              combination of both purposes (B-1/B-2).
            </p>
          </div>
          <div className="bg-[#F3F3F1] border border-slate-200 p-8 md:p-10 h-fit">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-6">
              What you'll get
            </p>
            <ul className="space-y-4">
              {WHAT_YOU_GET.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-slate-900" />
                  {g}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="pt-14"
        >
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900">
            Documents required for a US tourist visa
          </h2>
          <p className="mt-3 text-sm text-slate-500">
            When applying for a US Tourist Visa, you must submit the following:
          </p>
          <div className="grid md:grid-cols-2 gap-x-14 mt-6">
            {US_DOCS.map((d) => (
              <div
                key={d.item}
                className="py-5 border-b border-slate-100"
                data-testid={`us-doc-${d.item.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-slate-900" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{d.item}</p>
                    {d.note && (
                      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d.note}</p>
                    )}
                    {d.children && (
                      <ul className="mt-3 space-y-2">
                        {d.children.map((c) => (
                          <li
                            key={c}
                            className="text-sm text-slate-500 leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-1.5 before:h-px before:bg-slate-400"
                          >
                            {c}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-16 flex flex-wrap items-center justify-between gap-8 bg-white border border-slate-200 p-8 md:p-10"
        >
          <div>
            <p className="font-display text-2xl tracking-tight text-slate-900">
              The DS-160, done for you.
            </p>
            <p className="text-sm text-slate-500 mt-2 max-w-md leading-relaxed">
              Tell us about your trip and a specialist prepares your complete
              US visa file — form, documents and interview-ready itinerary.
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="us-visa-cta-button"
            className="group flex items-center gap-3 bg-slate-900 text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-slate-700 transition-colors duration-300"
          >
            Start your US visa file
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </main>

    <Footer onNavigate={(t) => (window.location.href = `/${t}`)} />
  </div>
);
